// routes/PublicRoutes.jsx
import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import PublicLayout from '../components/layout/PublicLayout';

// Public Pages
import Home from '../pages/public/Home';
import About from '../pages/public/About';
import Services from '../pages/public/Services';
import Contact from '../pages/public/Contact';
import Emergency from '../pages/public/Emergency';
import HospitalFinder from '../pages/public/HospitalFinder';
import PharmacyFinder from '../pages/public/PharmacyFinder';
import DoctorDirectory from '../pages/public/DoctorDirectory';
import NotFound from '../pages/public/NotFound';
import HospitalDetail from '../pages/public/HospitalDetail';
import BookConsultation from '../pages/public/BookConsultation';
import BookHospitalConsultation from '../pages/public/BookHospitalConsultation';
import PharmacyDetail from '../pages/public/PharmacyDetail';

// Ambulance Pages
import AmbulanceRequestForm from '../pages/public/AmbulanceRequestForm';
import AmbulanceDetail from '../pages/public/AmbulanceDetail';
import MyAmbulanceRequests from '../pages/public/MyAmbulanceRequests';
import AmbulanceRequestDetail from '../pages/public/AmbulanceRequestDetail';

const PublicRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<PublicLayout />}>
        <Route index element={
          <>
            <Helmet>
              <title>MediGo - Your Healthcare Companion</title>
              <meta name="description" content="Find hospitals, pharmacies, doctors, and emergency services near you. Book consultations, order medicines online with MediGo." />
              <meta name="keywords" content="healthcare, hospitals, pharmacies, doctors, emergency services, medical consultation" />
              <meta property="og:title" content="MediGo - Your Healthcare Companion" />
              <meta property="og:description" content="Find hospitals, pharmacies, doctors, and emergency services near you." />
              <meta property="og:type" content="website" />
              <link rel="canonical" href="https://MediGo.com/" />
            </Helmet>
            <Home />
          </>
        } />
        
        <Route path="about" element={
          <>
            <Helmet>
              <title>About Us - MediGo Healthcare Platform</title>
              <meta name="description" content="Learn about MediGo's mission to make healthcare accessible and convenient for everyone." />
              <link rel="canonical" href="https://MediGo.com/about" />
            </Helmet>
            <About />
          </>
        } />
        
        <Route path="services" element={
          <>
            <Helmet>
              <title>Our Services - MediGo Healthcare Solutions</title>
              <meta name="description" content="Discover MediGo's comprehensive healthcare services including hospital finder, pharmacy locator, and online consultations." />
              <link rel="canonical" href="https://MediGo.com/services" />
            </Helmet>
            <Services />
          </>
        } />
        
        <Route path="contact" element={
          <>
            <Helmet>
              <title>Contact Us - MediGo Support</title>
              <meta name="description" content="Get in touch with MediGo support team for any questions or assistance." />
              <link rel="canonical" href="https://MediGo.com/contact" />
            </Helmet>
            <Contact />
          </>
        } />
        
        <Route path="emergency" element={
          <>
            <Helmet>
              <title>Emergency Services - MediGo Ambulance Booking</title>
              <meta name="description" content="Request emergency ambulance services through MediGo. Fast, reliable emergency medical transportation." />
              <link rel="canonical" href="https://MediGo.com/emergency" />
            </Helmet>
            <Emergency />
          </>
        } />
        
        <Route path="hospitals" element={
          <>
            <Helmet>
              <title>Find Hospitals Near You - MediGo Hospital Directory</title>
              <meta name="description" content="Find the best hospitals near your location. View ratings, services, and book appointments online." />
              <link rel="canonical" href="https://MediGo.com/hospitals" />
            </Helmet>
            <HospitalFinder />
          </>
        } />

        <Route path="hospitals/:id" element={
          <>
            <Helmet>
              <title>Hospital Details - MediGo</title>
              <meta name="description" content="View detailed information about this hospital including services, facilities, and contact information." />
            </Helmet>
            <HospitalDetail />
          </>
        } />

        {/* NEW: Hospital Consultation Booking Route */}
        <Route path="hospitals/:hospitalId/book" element={
          <>
            <Helmet>
              <title>Book Hospital Consultation - MediGo</title>
              <meta name="description" content="Book a physical consultation at the hospital. Select a doctor and schedule your appointment." />
            </Helmet>
            <BookHospitalConsultation />
          </>
        } />

        <Route path="pharmacies" element={
          <>
            <Helmet>
              <title>Pharmacy Locator - Find Pharmacies Near You | MediGo</title>
              <meta name="description" content="Locate pharmacies near you, check medicine availability, and order prescriptions online." />
              <link rel="canonical" href="https://MediGo.com/pharmacies" />
            </Helmet>
            <PharmacyFinder />
          </>
        } />

        <Route path="pharmacies/:id" element={
          <>
            <Helmet>
              <title>Pharmacy Details - MediGo</title>
              <meta name="description" content="View detailed information about this pharmacy including products, delivery options, and contact information." />
            </Helmet>
            <PharmacyDetail />
          </>
        } />
        
        <Route path="doctors" element={
          <>
            <Helmet>
              <title>Doctor Directory - Find Specialists Online | MediGo</title>
              <meta name="description" content="Browse doctors by specialization, read reviews, and book online consultations." />
              <link rel="canonical" href="https://MediGo.com/doctors" />
            </Helmet>
            <DoctorDirectory />
          </>
        } />

        <Route path="book-consultation/:doctorId" element={
          <>
            <Helmet>
              <title>Book Consultation - MediGo</title>
              <meta name="description" content="Book a consultation with our qualified doctors online or in-person." />
            </Helmet>
            <BookConsultation />
          </>
        } />

        {/* Ambulance Routes */}
        <Route path="ambulances/request" element={
          <>
            <Helmet>
              <title>Request Ambulance - MediGo Emergency Services</title>
              <meta name="description" content="Request an ambulance service. Fill in patient and location details for quick emergency response." />
              <link rel="canonical" href="https://MediGo.com/ambulances/request" />
            </Helmet>
            <AmbulanceRequestForm />
          </>
        } />

        <Route path="ambulances/:id" element={
          <>
            <Helmet>
              <title>Ambulance Details - MediGo</title>
              <meta name="description" content="View detailed information about this ambulance service including pricing, equipment, and ratings." />
            </Helmet>
            <AmbulanceDetail />
          </>
        } />

        <Route path="ambulances/my-requests" element={
          <>
            <Helmet>
              <title>My Ambulance Requests - MediGo</title>
              <meta name="description" content="View and manage your ambulance service requests and their status." />
              <link rel="canonical" href="https://MediGo.com/ambulances/my-requests" />
            </Helmet>
            <MyAmbulanceRequests />
          </>
        } />

        <Route path="ambulances/requests/:id" element={
          <>
            <Helmet>
              <title>Request Details - MediGo</title>
              <meta name="description" content="View detailed information about your ambulance request and track its status." />
            </Helmet>
            <AmbulanceRequestDetail />
          </>
        } />
        
        {/* 404 Route */}
        <Route path="*" element={
          <>
            <Helmet>
              <title>404 Not Found - MediGo</title>
              <meta name="robots" content="noindex, nofollow" />
              <link rel="canonical" href="https://MediGo.com/404" />
            </Helmet>
            <NotFound />
          </>
        } />
      </Route>
    </Routes>
  );
};

export default PublicRoutes;